import React from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import * as LucideIcons from "lucide-react";

export interface PriorityTile {
  id: string;
  label: string;
  icon?: string;
  image?: string;
  /** Column span in 12-column grid (1-12), default 3 */
  colSpan?: number;
  /** Row span in grid (1-12), default 1 */
  rowSpan?: number;
  /** Image position X (0-100%), default 50 */
  imagePositionX?: number;
  /** Image position Y (0-100%), default 50 */
  imagePositionY?: number;
  /** Image scale/zoom (100 = 100%), default 100 */
  imageScale?: number;
  /** Title position: 'top' or 'bottom', default based on rowSpan */
  titlePosition?: 'top' | 'bottom';
  /** Checkbox corner position, default 'bottom-right' */
  checkboxPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

interface PriorityTilesProps {
  tiles: PriorityTile[];
  selectedIds: string[];
  animated?: boolean;
  className?: string;
  /** Whether the customer can interactively select tiles */
  interactive?: boolean;
  /** Callback when a tile is clicked (for interactive mode) */
  onTileClick?: (id: string) => void;
  /** Currently hovered tile ID (for cross-view sync) */
  hoveredTileId?: string | null;
  /** Callback when tile hover state changes */
  onTileHover?: (id: string | null) => void;
}

export function PriorityTiles({
  tiles,
  selectedIds = [],
  animated = true,
  className,
  interactive = false,
  onTileClick,
  hoveredTileId,
  onTileHover,
}: PriorityTilesProps) {
  const getIcon = (iconName?: string) => {
    if (!iconName) return null;
    const Icon = (LucideIcons as any)[iconName];
    return Icon ? <Icon className="h-12 w-12" /> : null;
  };

  const isSelected = (id: string) => selectedIds.includes(id);
  const isHovered = (id: string) => hoveredTileId === id;

  const handleClick = (id: string) => {
    if (interactive && onTileClick) {
      onTileClick(id);
    }
  };

  const handleMouseEnter = (id: string) => {
    onTileHover?.(id);
  };

  const handleMouseLeave = () => {
    onTileHover?.(null);
  };

  return (
    <div className={cn("grid grid-cols-12 gap-3 auto-rows-[80px]", className)}>
      {tiles.map((tile, index) => {
        const colSpan = tile.colSpan || 3;
        const rowSpan = tile.rowSpan || 2;
        const hasImage = !!tile.image;
        const posX = tile.imagePositionX ?? 50;
        const posY = tile.imagePositionY ?? 50;
        const scale = tile.imageScale ?? 100;
        
        // Title position: use explicit setting or default based on rowSpan
        const titlePos = tile.titlePosition || (rowSpan <= 2 ? 'top' : 'bottom');
        
        // Checkbox position: default to bottom-right
        const checkboxPos = tile.checkboxPosition || 'bottom-right';
        const checkboxClasses = {
          'top-left': 'top-2 left-2',
          'top-right': 'top-2 right-2',
          'bottom-left': 'bottom-2 left-2',
          'bottom-right': 'bottom-2 right-2',
        }[checkboxPos];
        
        return (
          <div
            key={tile.id}
            onClick={() => handleClick(tile.id)}
            onMouseEnter={() => handleMouseEnter(tile.id)}
            onMouseLeave={handleMouseLeave}
            className={cn(
              "relative overflow-hidden transition-all duration-300 rounded-lg",
              "border-2 border-primary/20 bg-white",
              // Apply hover styles for both local hover and synced hover
              (isHovered(tile.id)) && "border-primary/40 shadow-lg scale-[1.02]",
              "hover:border-primary/40 hover:shadow-lg hover:scale-[1.02]",
              isSelected(tile.id) && "border-primary shadow-lg ring-2 ring-primary/30",
              "cursor-pointer",
              animated && "animate-in fade-in-50 slide-in-from-bottom-4"
            )}
            style={{
              gridColumn: `span ${colSpan}`,
              gridRow: `span ${rowSpan}`,
              animationDelay: animated ? `${index * 80}ms` : undefined,
            }}
          >
            {/* Image tiles with title bar */}
            {hasImage ? (
              <div className="h-full flex flex-col">
                {/* Title bar - TOP */}
                {titlePos === 'top' && (
                  <div className="flex-shrink-0 bg-primary text-primary-foreground px-3 py-2 font-semibold text-sm rounded-t-lg">
                    {tile.label}
                  </div>
                )}
                
                {/* Image fills remaining space */}
                <div className={cn(
                  "flex-1 min-h-0 overflow-hidden",
                  titlePos === 'top' ? "rounded-b-lg" : "rounded-t-lg"
                )}>
                  <img
                    src={tile.image}
                    alt={tile.label}
                    className="w-full h-full object-cover"
                    style={{
                      objectPosition: `${posX}% ${posY}%`,
                      transform: `scale(${scale / 100})`,
                      transformOrigin: `${posX}% ${posY}%`,
                    }}
                  />
                </div>

                {/* Title bar at BOTTOM */}
                {titlePos === 'bottom' && (
                  <div className="flex-shrink-0 bg-primary text-primary-foreground px-3 py-3 font-semibold text-sm rounded-b-lg">
                    {tile.label}
                  </div>
                )}
              </div>
            ) : (
              /* Icon-only tiles */
              <div className="h-full flex flex-col">
                {/* Icon area */}
                <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-muted/50 to-muted">
                  <div className={cn(
                    "p-4 rounded-full",
                    isSelected(tile.id)
                      ? "bg-primary/10 text-primary"
                      : "bg-muted-foreground/10 text-muted-foreground"
                  )}>
                    {getIcon(tile.icon)}
                  </div>
                </div>
                
                {/* Label bar */}
                <div className={cn(
                  "px-3 py-2 text-sm font-semibold text-center",
                  isSelected(tile.id)
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}>
                  {tile.label}
                </div>
              </div>
            )}

            {/* Selection checkbox with configurable position */}
            <div className={cn(
              "absolute h-6 w-6 rounded-sm border-2 flex items-center justify-center z-20 transition-colors",
              checkboxClasses,
              isSelected(tile.id)
                ? "bg-primary border-primary"
                : "bg-white/90 border-primary/50"
            )}>
              {isSelected(tile.id) && (
                <Check className="h-4 w-4 text-primary-foreground" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
